import { Observable } from 'rxjs'
import { ofType } from 'redux-observable'
import { map, concatMap } from 'rxjs/operators'
import { hardwareKeyDown, hardwareKeyUp, mouseDown, releasePressedKeys, TriState,
  uncheckActive, uncheckAlwaysOn, windowBlur, mouseWheel } from '../actions/toolbar'
import { KeyPressEvents } from './serialize'
import { ModifierFlags } from './modifiers'
import { mapKeyboardEvent2KeyMessage,
  mapModifierKey2KeyMessage as dep_mapModifierKey2KeyMessage,
  transformToAction as dep_transformToAction, // eslint-disable-line
  transformToActionDebug as dep_transformToActionD } from './transform'
import { filterPasteShortcuts$ } from './shortcuts'

/* eslint no-use-before-define: 0 */

const defaultDeps = {
  modifierFlags: new ModifierFlags(),
  transformToAction: dep_transformToActionD,
  mapModifierKey2KeyMessage: dep_mapModifierKey2KeyMessage,
  getActiveModifiers: (modifiers) => getModifiersWithState(modifiers, TriState.Active),
  getAlwaysOnModifiers: (modifiers) => getModifiersWithState(modifiers, TriState.AlwaysOn)
}

let deps = defaultDeps

/**
 * use just for testing
 * resets ModifierFlags() dependency
 **/
export function injectDeps (dependencies) {
  deps = {
    ...defaultDeps,
    modifierFlags: new ModifierFlags(),
    ...dependencies,
  }
}

/**
 * @param {*} modifiers - the modifiers from toolbar reducer
 * @param {*} modifierState
 * @returns all the modifier key names with the given modifierState
 */
export function getModifiersWithState (modifiers, modifierState) {
  return Object.keys(modifiers)
    .filter((m) => modifiers[m] === modifierState)
}

export function handleAlwaysOnModifierDownEvents (action$, state$) {
  const modifierAlwaysOnDown$ = action$.pipe(
    ofType(hardwareKeyDown, uncheckActive, mouseDown, mouseWheel),
    map(() => {
      const modifiersWillBeDown = deps.getAlwaysOnModifiers(state$.value.toolbar.modifiers)
        .filter((modifierKey) => !deps.modifierFlags.isDown(modifierKey))
      deps.modifierFlags.setDownFor(modifiersWillBeDown)
      return modifiersWillBeDown
    }),
    concatMap((groups) => groups),
    map((modifierKey) => deps.mapModifierKey2KeyMessage(KeyPressEvents.KEY_DOWN, modifierKey)),
    map((data) => deps.transformToAction(data))
  )

  return modifierAlwaysOnDown$
}

export function handleAlwaysOnModifierUpEvents (action$) {
  const modifierAlwaysOnUp$ = action$.pipe(
    ofType(uncheckAlwaysOn),
    map((action) => {
      const modifierKeyName = action.payload
      deps.modifierFlags.resetDown(modifierKeyName)
      return [modifierKeyName]
    }),
    concatMap((groups) => groups),
    map((modifierKeyName) => deps.mapModifierKey2KeyMessage(KeyPressEvents.KEY_UP, modifierKeyName)),
    map((data) => deps.transformToAction(data)),
  )

  return modifierAlwaysOnUp$
}

export function handleActiveModifierDownEvents (action$, state$) {
  return action$.pipe(
    ofType(hardwareKeyDown, uncheckActive, mouseDown, mouseWheel),
    map(() => deps.getActiveModifiers(state$.value.toolbar.modifiers)),
    concatMap((groups) => groups),
    map((modifierKey) => deps.mapModifierKey2KeyMessage(KeyPressEvents.KEY_DOWN, modifierKey)),
    map((data) => deps.transformToAction(data)),
  )
}

export function handleActiveModifierUpEvents (action$, state$) {
  return action$.pipe(
    ofType(hardwareKeyUp, uncheckActive, mouseDown, mouseWheel),
    map(() => deps.getActiveModifiers(state$.value.toolbar.modifiers).reverse()),
    concatMap((groups) => groups),
    map((modifierKey) => deps.mapModifierKey2KeyMessage(KeyPressEvents.KEY_UP, modifierKey)),
    map((data) => deps.transformToAction(data)),
  )
}

function createKeyDown$ (action$) {
  const keyDown$ = action$.pipe(
    ofType(hardwareKeyDown),
    map((action) => action.payload),
    map((keyboardEvent) => mapKeyboardEvent2KeyMessage(keyboardEvent))
  )
  return keyDown$
}

function createKeyUp$ (action$) {
  const keyUp$ = action$.pipe(
    ofType(hardwareKeyUp),
    map((action) => {
      const key = action.payload.key
      if (deps.modifierFlags.isKeyAModifier(key)) {
        deps.modifierFlags.resetDown(key)
      }
      return action.payload
    }),
    map((keyboardEvent) => mapKeyboardEvent2KeyMessage(keyboardEvent))
  )
  return keyUp$
}

function createResetKeys$ (action$) {
  const resetKeys$ = action$.pipe(
    ofType(windowBlur, releasePressedKeys),
    map(() => {
      deps.modifierFlags.resetDownForAll()
      return { action: KeyPressEvents.RESET_PRESSED_KEYS }
    }),
  )
  return resetKeys$
}

export function handleKeyboardEvents (action$, state$) {
  const keyboard$ = filterPasteShortcuts$(
    Observable.merge(
      createKeyDown$(action$),
      createKeyUp$(action$),
      createResetKeys$(action$),
    ),
    state$
  )
    .map((data) => deps.transformToAction(data))

  return keyboard$
}

export const ForUnitTest = {
  getModifiersWithState,
  createKeyDown$,
  createKeyUp$,
  createResetKeys$,
}
